/**
 * Tutorial Sound Effects Manager
 * Handles playing different sound effects for tutorial loss scenarios
 */

export enum LossType {
  CAPTURE = 'capture',    // Piece was captured (slash effect)
  RACE = 'race',         // Enemy reached goal (pulse effect)  
  GENERAL = 'general'     // General loss (shutter effect)
}

export class TutorialSoundEffects {
  private audioCache: Map<LossType, HTMLAudioElement> = new Map();
  private isEnabled: boolean = true;
  private volume: number = 0.7;

  constructor() {
    this.preloadSounds();
  }

  /**
   * Preload all sound effects for better performance
   */
  private preloadSounds(): void {
    try {
      // Create audio elements for each loss type
      const captureAudio = new Audio("/sounds/capture_loss.mp3");  // Sharp, decisive sound - piece taken
      const raceAudio = new Audio("/sounds/race_loss.mp3");        // Building tension sound - enemy advancing  
      const generalAudio = new Audio("/sounds/general_loss.mp3");  // General failure sound
      
      // Set volume for all sounds
      captureAudio.volume = this.volume;
      raceAudio.volume = this.volume;
      generalAudio.volume = this.volume;
      
      // Cache the audio elements
      this.audioCache.set(LossType.CAPTURE, captureAudio);
      this.audioCache.set(LossType.RACE, raceAudio);
      this.audioCache.set(LossType.GENERAL, generalAudio);
      
    } catch (error) {
      this.isEnabled = false;
    }
  }

  /**
   * Play sound effect for specific loss type
   * @param lossType The type of loss that occurred
   */
  public playLossSound(lossType: LossType): void {
    if (!this.isEnabled) return;

    const audio = this.audioCache.get(lossType);
    if (!audio) {
      return;
    }

    try {
      // Reset audio to beginning in case it was already playing
      audio.currentTime = 0;
      
      // Play the sound
      audio.play().catch(error => {
      });
      
    } catch (error) {
    }
  }

  /**
   * Set volume for all sound effects (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    
    this.audioCache.forEach(audio => {
      audio.volume = this.volume;
    });
  }

  /**
   * Enable or disable sound effects
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Get current enabled state
   */
  public getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Determine loss type based on tutorial failure reason
   * @param constellation Tutorial constellation
   * @param tutorialIndex Tutorial index (0-based)
   * @param failureReason Reason for the failure
   * @returns Appropriate loss type for sound effect
   */
  public static getLossTypeFromFailure(
    constellation: string, 
    tutorialIndex: number, 
    failureReason: string
  ): LossType {
    const reason = failureReason.toLowerCase();
    
    // Check for piece capture scenarios
    if (reason.includes('captured') || reason.includes('taken') || reason.includes('lost')) {
      return LossType.CAPTURE;
    }
    
    // Check for race/reaching scenarios  
    if (reason.includes('reached') || reason.includes('row 0') || reason.includes('row 7') || 
        reason.includes('race') || reason.includes('escaped')) {
      return LossType.RACE;
    }
    
    // Default to general loss sound
    return LossType.GENERAL;
  }
}

// Create singleton instance for global use
export const tutorialSoundEffects = new TutorialSoundEffects();